var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useRef, useState } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import { t } from '@lingui/core/macro';
import { useToast } from '@pocketlaw/tetris';
import { GlobalSearchDocument } from 'app/domains/global-search/hooks/useSearchQuery';
import { getToastTexts } from './getToastTexts';
import { isPropertyIdentical } from './isPropertyIndentical';
import { PollBulkMetadataPropertyDocument } from './usePollBulkMetadataProperty.gql';
import { useTableContext } from '../../../SearchResultsTable/TableContext';
const POLL_INTERVAL = 1500;
const MAX_ATTEMPTS = 10;
export const usePollBulkMetadataProperty = () => {
    const { setSelected } = useTableContext();
    const { addToast } = useToast();
    const client = useApolloClient();
    const dataRef = useRef(null);
    const attemptCount = useRef(0);
    const [pollingState, setPollingState] = useState({ state: 'idle' });
    const toastAction = {
        label: t({
            message: 'Close',
            comment: 'Bulk action: Metadata - Toast close button',
        }),
        onClick: () => { },
    };
    const handleError = (stopCallback) => {
        setPollingState({ state: 'idle' });
        stopCallback();
        if (!dataRef.current) {
            return;
        }
        const { title, message } = getToastTexts({
            status: 'error',
            type: dataRef.current.value === null ? 'clear' : 'update',
            documentCount: dataRef.current.documentCount,
            property: dataRef.current.property,
        });
        addToast({ title, message, action: toastAction, appearance: 'danger' });
        setSelected({ type: 'reset' });
        dataRef.current = null;
    };
    const handleSuccess = (stopCallback) => {
        setPollingState({ state: 'idle' });
        stopCallback();
        if (!dataRef.current) {
            return;
        }
        const { title, message } = getToastTexts({
            status: 'success',
            type: dataRef.current.value === null ? 'clear' : 'update',
            documentCount: dataRef.current.documentCount,
            property: dataRef.current.property,
        });
        addToast({ title, message, action: toastAction, appearance: 'success' });
        setSelected({ type: 'reset' });
        dataRef.current = null;
    };
    const [query, state] = useLazyQuery(PollBulkMetadataPropertyDocument, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onError: () => {
            handleError(state.stopPolling);
        },
        onCompleted: (data) => __awaiter(void 0, void 0, void 0, function* () {
            const ref = dataRef.current;
            if (!ref) {
                return;
            }
            const isUpdated = data.search.results.every((result) => {
                const key = `metadataDocument${ref.property.name}`;
                const value = result[key];
                return isPropertyIdentical(ref.value, value);
            });
            if (isUpdated) {
                yield client.refetchQueries({
                    include: [GlobalSearchDocument],
                    onQueryUpdated(observableQuery) {
                        return observableQuery.refetch();
                    },
                });
                handleSuccess(state.stopPolling);
            }
            else {
                attemptCount.current += 1;
                if (attemptCount.current > MAX_ATTEMPTS) {
                    handleError(state.stopPolling);
                }
                else {
                    state.startPolling(POLL_INTERVAL);
                }
            }
        }),
    });
    const startPolling = (options) => {
        const { documentIds, property, value } = options;
        dataRef.current = { property, value, documentCount: documentIds.length };
        attemptCount.current = 0;
        setPollingState({
            state: 'polling',
            type: options.type,
            documentCount: documentIds.length,
            property,
        });
        query({
            variables: {
                query: '',
                referrer: 'bulkMetadataPolling',
                limit: documentIds.length,
                where: {
                    entityId: {
                        in: documentIds,
                    },
                },
            },
        });
    };
    const stopPolling = () => {
        setPollingState({ state: 'idle' });
        state.stopPolling();
    };
    return {
        pollingState,
        startPolling,
        stopPolling,
    };
};
